import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useCustomQuery} from "~/query/queryClient";
import {queryHttp} from "~/query/queryHttp";
import type {m_taxonomy} from "~/services/classes/TaxonomyClasses";
import {notificationStore} from "~/stores/init";
import {
    taxonomyDetailUrl,
    taxonomyGotoHistoryUrl,
    taxonomyHistoryUrl,
} from "~/pages/taxonomy-builder-v2/services/taxonomyBuilderUrls";

/** The history's query key: saving an edit refreshes it (`store_state` drops the redo entries). */
export const taxonomyHistoryQueryKey = (taxonomyId: number) => [taxonomyHistoryUrl(taxonomyId)] as const;

/**
 * Undo and redo through the taxonomy's history, as the current builder does (`TaxonomyManagerStore`):
 * undo goes to the operation before the latest one up to the active state (the first operation can't be
 * undone), redo to the first operation after it. Either replaces the cached taxonomy with the response.
 */
export function useTaxonomyHistory(taxonomy: m_taxonomy.FullSerializer) {
    const queryClient = useQueryClient();
    const {data: history} = useCustomQuery({
        queryKey: taxonomyHistoryQueryKey(taxonomy.id),
        queryFn: async () => (await queryHttp.get<m_taxonomy.SimpleTaxonomyOperationSerializer[]>(taxonomyHistoryUrl(taxonomy.id))).data,
    });
    const {mutate, isPending} = useMutation({
        mutationFn: async (gotoNumber: number) => {
            const body: m_taxonomy.GotoHistorySerializer = {goto_history_number: gotoNumber};
            return (await queryHttp.patch<m_taxonomy.FullSerializer>(taxonomyGotoHistoryUrl(taxonomy.id), body)).data;
        },
        onSuccess: updated => {
            queryClient.setQueryData([taxonomyDetailUrl(updated.id)], updated);
            void queryClient.invalidateQueries({queryKey: taxonomyHistoryQueryKey(updated.id)});
        },
        onError: () => notificationStore.show('Cannot undo or redo the change. Please try again.', {
            severity: 'error',
            id: `taxonomy-goto-history-error-${taxonomy.id}`,
            autoHideDuration: 6000,
        }),
    });

    const current = taxonomy.current_operation_number;
    const operations = [...(history ?? [])].sort((a, b) => a.operation_number - b.operation_number);
    // Up to the active state, without the first operation; undoing the last of them goes to the one before.
    const undoable = operations.filter((o, i) => i >= 1 && o.operation_number <= current);
    const undoTo = undoable.length === 0 ? undefined : operations[operations.indexOf(undoable[undoable.length - 1]) - 1];
    const redoTo = operations.find(o => o.operation_number > current);

    return {
        canUndo: !isPending && undoTo !== undefined,
        canRedo: !isPending && redoTo !== undefined,
        isPending,
        undo: () => undoTo && mutate(undoTo.operation_number),
        redo: () => redoTo && mutate(redoTo.operation_number),
    };
}
